import React, { useEffect } from 'react';
import { Alert, Col, Form, Input, Row, Space, Spin } from 'antd';
import FormItem from '@/common/FormItem';
import CustomModal from '@/common/CustomModal';
import CustomButton from '@/common/CustomButton';
import styles from '@/common/Layout/index.module.less';

const { TextArea } = Input;

/**
 * Material Label Reprint — QA approval modal.
 *
 * Visually identical to the existing "Verification By QA" modal used at
 * vehicle storage, document verification, batch update, weighment, dispense,
 * QC approve, sampling, and line clearance touchpoints
 * (see features/material/components/VerificationModal/index.js). Same Form
 * layout, same FormItem rules, same Space + CustomButton footer.
 *
 * This is a focused component — only User ID + Password + Comments — without
 * the page-branch payload logic the giant VerificationModal carries.
 *
 * Props:
 *   visible       bool
 *   onCancel      () => void
 *   onSubmit      ({ employee_id, password, comments }) => void
 *   loading       bool   — disables the form while the parent API call is in flight
 *   errorMessage  string — server-side error to display inline
 */
const LabelReprintApprovalModal = ({
  visible,
  onCancel,
  onSubmit,
  loading = false,
  errorMessage = ''
}) => {
  const [form] = Form.useForm();

  // Reset on open so a stale password from a previous attempt is not
  // submitted accidentally.
  useEffect(() => {
    if (visible) {
      form.resetFields();
    }
  }, [visible, form]);

  // On a server-side rejection (wrong password, non-QA approver, etc.) clear
  // the password so the QA user has to retype it.
  useEffect(() => {
    if (errorMessage) {
      form.setFieldsValue({ password: '' });
    }
  }, [errorMessage, form]);

  const onFinish = (values) => {
    onSubmit({
      employee_id: String(values.employee_id || '').trim(),
      password: String(values.password || ''),
      comments: String(values.comments || '').trim()
    });
  };

  const handleClose = () => {
    form.resetFields();
    onCancel && onCancel();
  };

  return (
    <CustomModal
      title="Verification By QA"
      visible={visible}
      width="50%"
      footer={null}
      className={styles.customModal}
      closable={false}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24}>
          <Col span={12}>
            <FormItem
              label="User ID"
              name="employee_id"
              rules={[
                {
                  required: true,
                  message: 'Enter User ID'
                }
              ]}
              className={styles.customLable}
            >
              <Input placeholder="Enter User ID" disabled={loading} />
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem
              label="Password"
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Enter Password'
                }
              ]}
              className={styles.customLable}
            >
              <Input.Password placeholder="Enter Password" disabled={loading} />
            </FormItem>
          </Col>
          <Col span={24}>
            <FormItem
              label="Comments"
              name="comments"
              rules={[
                {
                  required: true,
                  message: 'Enter Comments'
                }
              ]}
              className={styles.customLable}
            >
              <TextArea placeholder="Enter Comments" disabled={loading} />
            </FormItem>
          </Col>
        </Row>

        {errorMessage ? (
          <Alert
            type="error"
            showIcon
            message={errorMessage}
            style={{ marginBottom: 12 }}
          />
        ) : null}

        <FormItem className="text-center">
          <Space>
            <CustomButton
              className={styles.inwardButton}
              onClick={handleClose}
              type="primary"
              size="small"
              ghost
              disabled={loading}
            >
              Close
            </CustomButton>
            <CustomButton
              className={styles.inwardButton}
              htmlType="submit"
              type="primary"
              size="small"
              disabled={loading}
            >
              Verify
            </CustomButton>
            {loading && <Spin size="small" />}
          </Space>
        </FormItem>
      </Form>
    </CustomModal>
  );
};

export default LabelReprintApprovalModal;
